/**
 * Household vocabulary — the ONE enum (W6.A · D-A2).
 *
 * Before this file there were four parallel vocabularies for "what kind of
 * household member is this":
 *
 *   1. the capture widget's `'partner' | 'child' | 'pet'`
 *   2. the brain catalog's `member_types_applicable: ['talent', 'partner', 'child']`
 *   3. `platform.household_members.type` — an unconstrained varchar(30)
 *   4. `platform.household_members.relation` — a second unconstrained varchar(30)
 *
 * They disagreed on the most important member of all. The catalog says the
 * APPLICANT is `'talent'`; the widget never emits a `'talent'` member; the
 * roster therefore never held one, and the checklist engine filtered every
 * applicant-scoped task out of every kit ever generated — nobody was asked for
 * their own passport.
 *
 * The canonical set below keeps the catalog's word (`'talent'`) because that is
 * the vocabulary the served JSON already speaks, and adds the applicant to the
 * roster as a real row instead of as an absence.
 *
 * It lives in `foundation`, not `locale`, because `@tonnie/locale` is
 * bundler-only and brain-service — which owns the checklist engine — cannot
 * import it (H9 / DW6-1).
 */

// ═══════════════════════════════════════════════════════════════════════
// MEMBER TYPE
// ═══════════════════════════════════════════════════════════════════════

/**
 * Every kind of household member. `'talent'` is the applicant themselves —
 * the "self row". It is a real `household_members` row, not a NULL.
 */
export const HOUSEHOLD_MEMBER_TYPES = [
  'talent',
  'partner',
  'child',
  'pet',
  'extended_family',
  'other',
] as const;

export type HouseholdMemberType = (typeof HOUSEHOLD_MEMBER_TYPES)[number];

/** The applicant's own member type. */
export const SELF_MEMBER_TYPE: HouseholdMemberType = 'talent';

/** The `relation` value carried by the applicant's own row. */
export const SELF_MEMBER_RELATION = 'self';

export function isHouseholdMemberType(value: unknown): value is HouseholdMemberType {
  return typeof value === 'string' && (HOUSEHOLD_MEMBER_TYPES as readonly string[]).includes(value);
}

/**
 * Narrow an arbitrary stored `type` onto the enum. Rows written before the
 * vocabulary was unified can carry anything the varchar allowed, so an
 * unrecognised value resolves to `'other'` rather than leaking out untyped.
 */
export function toHouseholdMemberType(value: unknown): HouseholdMemberType {
  return isHouseholdMemberType(value) ? value : 'other';
}

/** Is this the applicant's own row? */
export function isSelfMember(member: { type?: string | null }): boolean {
  return member.type === SELF_MEMBER_TYPE;
}

/**
 * The members a *family* projection is about: everyone except the applicant.
 *
 * `familyConfig.hasPartner` / `childrenCount` / `hasPets` and every
 * "who is relocating with you" count are statements about the people around
 * the applicant. Now that the applicant is a row, every such projection must
 * exclude it or a solo citizen reads as a household of one dependant.
 */
export function familyMembersOf<T extends { type?: string | null }>(members: readonly T[]): T[] {
  return members.filter((m) => !isSelfMember(m));
}

// ═══════════════════════════════════════════════════════════════════════
// NAMES
// ═══════════════════════════════════════════════════════════════════════

/**
 * The ONE place a display name becomes `firstName` / `lastName` (H2).
 *
 * There used to be three treatments of the same value: split on a single space
 * at write, re-joined with a single space at match, and underscore-collapsed in
 * the checklist engine. "Ana  Maria Silva" (two spaces) therefore failed to
 * match itself — it was stored as `Ana` + ` Maria Silva`, re-joined as
 * `Ana  Maria Silva`… and compared against the engine's `ana_maria_silva`.
 *
 * Every write path calls THIS. Structured first/last is the wire format from
 * here on; `splitDisplayName` exists only for the legacy single-`name` field
 * still accepted by the public DTO.
 */
export function splitDisplayName(name: string): { firstName: string; lastName: string | null } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: null };
  const [first, ...rest] = parts;
  return { firstName: first, lastName: rest.length > 0 ? rest.join(' ') : null };
}

/** The inverse. The ONE place first/last becomes a display string. */
export function joinDisplayName(member: {
  firstName?: string | null;
  lastName?: string | null;
}): string {
  return [member.firstName, member.lastName]
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .map((p) => p.trim())
    .join(' ');
}

// ═══════════════════════════════════════════════════════════════════════
// VISA PATHWAY
// ═══════════════════════════════════════════════════════════════════════

/**
 * Visa pathway CODES that the served country catalog may reference.
 *
 * Codes only — deliberately no human labels. The labels are a statement about
 * immigration law and belong to counsel, not to this repo: the shipped PT
 * catalog currently labels `d4` "Family Reunion", which is not what D4 is in
 * Portugal. That label is NOT corrected here (F44 — we do not ship a guess
 * about immigration law); this enum exists so the catalog gate can prove the
 * code SET has not drifted, without asserting anything about what the codes
 * mean.
 */
export const VISA_PATHWAY_CODES = [
  'd1',
  'd2',
  'd4',
  'd7',
  'd8',
  'work_permit',
  'eu_family',
  'startup',
  'tech_visa',
] as const;
